import BigNumber from 'bignumber.js'

import { assert } from '../assert/assert.js'
import { bigNumberify, NumberLike as NumberLike_ } from '../math/bigNumber.js'
import { NormalizedNumber } from './NormalizedNumber.js'

type NumberLike = NumberLike_ | NormalizedNumber
type Value = Percentage | number

const PERCENTAGE_PRECISION = new BigNumber(100)
const BIPS_PRECISION = new BigNumber(10000)

export interface Percentage {
  toBips(): BigNumber
  toBigNumber(): BigNumber

  abs(): Percentage
  eq(n: Value, base?: number): boolean
  comparedTo(n: Value, base?: number): number
  decimalPlaces(decimalPlaces: number, roundingMode?: BigNumber.RoundingMode): Percentage
  pow(n: number, m?: number): Percentage
  sqrt(): Percentage
  integerValue(rm?: BigNumber.RoundingMode): Percentage
  gt(n: Value, base?: number): boolean
  gte(n: Value, base?: number): boolean
  lt(n: Value, base?: number): boolean
  lte(n: Value, base?: number): boolean
  isNaN(): boolean
  isInteger(): boolean
  isFinite(): boolean
  isNegative(): boolean
  isPositive(): boolean
  isZero(): boolean
  minus(n: Value, base?: number): Percentage
  plus(n: Value, base?: number): Percentage
  mod(n: Value, base?: number): Percentage
  times(n: Value, base?: number): Percentage
  div(n: Value, base?: number): Percentage
  negated(): Percentage
  precision(significantDigits: number, roundingMode?: BigNumber.RoundingMode): Percentage
  shiftedBy(n: number): Percentage
  toExponential(): string
  toFixed(): string
  toFixed(decimalPlaces: number, roundingMode?: BigNumber.RoundingMode): string
  toFormat(format: BigNumber.Format): string
  formatAsPercentagePoints(): string
  formatAsBips(): string
  toFraction(maxDenominator?: number): [BigNumber, BigNumber]
  toJSON(): string
  toNumber(): number
  toPrecision(significantDigits: number, roundingMode?: BigNumber.RoundingMode): string
  toString(base?: number): string
  valueOf(): string
}

interface PercentageOptions {
  allowMoreThan1?: boolean
  allowLessThan0?: boolean
}

const operationOptions: PercentageOptions = {
  allowLessThan0: true,
  allowMoreThan1: true,
}

// Should be used only for type checking
export class PercentageClass implements Percentage {
  private readonly value: BigNumber
  private readonly asString: string

  constructor(value: NumberLike, { allowMoreThan1 = false, allowLessThan0 = false }: PercentageOptions = {}) {
    this.value = numberLikeToBigNumber(value)
    this.asString = this.value.toString()
    if (!allowLessThan0) {
      assert(this.value.gte(0), `Percentage value ${this.asString} should be greater than or equal to 0.`)
    }
    if (!allowMoreThan1) {
      assert(this.value.lte(1), `Percentage value ${this.asString} should be less than or equal to 1.`)
    }
  }

  static isInstance(value: unknown): value is Percentage {
    return value instanceof PercentageClass
  }

  static fromBips(value_: NumberLike): Percentage {
    const value = numberLikeToBigNumber(value_)
    return Percentage(value.div(BIPS_PRECISION))
  }

  static fromPercentagePoints(value_: number): Percentage {
    const value = numberLikeToBigNumber(value_)
    return Percentage(value.div(PERCENTAGE_PRECISION), { allowMoreThan1: true, allowLessThan0: true })
  }

  static min(...values: Percentage[]): Percentage {
    assert(values.length > 0, 'Requires at least 1 arg')
    return values.reduce((min, val) => (val.lt(min) ? val : min))
  }

  static max(...values: Percentage[]): Percentage {
    assert(values.length > 0, 'Requires at least 1 arg')
    return values.reduce((max, val) => (val.gt(max) ? val : max))
  }

  static sum(...values: Percentage[]): Percentage {
    return values.reduce((acc, val) => acc.plus(val), Percentage(0))
  }

  static sub(...values: Percentage[]): Percentage {
    assert(values.length > 0, 'Requires at least 1 arg')
    return values.reduce((acc, val) => acc.minus(val))
  }

  toBips(): BigNumber {
    return this.value.multipliedBy(BIPS_PRECISION)
  }

  toBigNumber(): BigNumber {
    return this.value
  }

  abs(): Percentage {
    return new Percentage(this.value.abs(), operationOptions)
  }

  comparedTo(n: Value, base?: number): number {
    return this.value.comparedTo(getValue(n), base)
  }

  decimalPlaces(decimalPlaces: number, roundingMode?: BigNumber.RoundingMode): Percentage {
    return new Percentage(this.value.decimalPlaces(decimalPlaces, roundingMode), operationOptions)
  }

  div(n: Value, base?: number): Percentage {
    return new Percentage(this.value.div(getValue(n), base), operationOptions)
  }

  pow(n: number, modulus?: number): Percentage {
    const m = modulus ? getValue(modulus) : undefined
    return new Percentage(this.value.pow(n, m), operationOptions)
  }

  sqrt(): Percentage {
    return new Percentage(this.value.sqrt(), operationOptions)
  }

  integerValue(rm?: BigNumber.RoundingMode): Percentage {
    return new Percentage(this.value.integerValue(rm), operationOptions)
  }

  eq(n: Value, base?: number): boolean {
    return this.value.eq(getValue(n), base)
  }

  gt(n: Value, base?: number): boolean {
    return this.value.gt(getValue(n), base)
  }

  gte(n: Value, base?: number): boolean {
    return this.value.gte(getValue(n), base)
  }

  lt(n: Value, base?: number): boolean {
    return this.value.lt(getValue(n), base)
  }

  lte(n: Value, base?: number): boolean {
    return this.value.lte(getValue(n), base)
  }

  isNaN(): boolean {
    return this.value.isNaN()
  }

  isInteger(): boolean {
    return this.value.isInteger()
  }

  isFinite(): boolean {
    return this.value.isFinite()
  }

  isNegative(): boolean {
    return this.value.isNegative()
  }

  isPositive(): boolean {
    return this.value.isPositive()
  }

  isZero(): boolean {
    return this.value.isZero()
  }

  minus(n: Value, base?: number): Percentage {
    return new Percentage(this.value.minus(getValue(n), base), operationOptions)
  }

  plus(n: Value, base?: number): Percentage {
    return new Percentage(this.value.plus(getValue(n), base), operationOptions)
  }

  mod(n: Value, base?: number): Percentage {
    return new Percentage(this.value.mod(getValue(n), base), operationOptions)
  }

  times(n: Value, base?: number): Percentage {
    return new Percentage(this.value.times(getValue(n), base), operationOptions)
  }

  negated(): Percentage {
    return new Percentage(this.value.negated(), operationOptions)
  }

  precision(significantDigits: number, roundingMode?: BigNumber.RoundingMode): Percentage {
    return new Percentage(this.value.precision(significantDigits, roundingMode), operationOptions)
  }

  shiftedBy(n: number): Percentage {
    return new Percentage(this.value.shiftedBy(n), operationOptions)
  }

  toExponential(): string {
    return this.value.toExponential()
  }

  toFixed(): string
  toFixed(decimalPlaces: number, roundingMode?: BigNumber.RoundingMode): string
  toFixed(decimalPlaces?: number, roundingMode?: BigNumber.RoundingMode): string {
    if (decimalPlaces === undefined) {
      return this.value.toFixed()
    }
    return this.value.toFixed(decimalPlaces, roundingMode)
  }

  toFormat(format: BigNumber.Format): string {
    return this.value.toFormat(format)
  }

  formatAsPercentagePoints(): string {
    return this.value.times(PERCENTAGE_PRECISION).toFormat({
      decimalSeparator: '.',
      suffix: '%',
      groupSeparator: '_',
      groupSize: 3,
    })
  }

  formatAsBips(): string {
    return this.toBips().toFormat({
      decimalSeparator: '.',
      suffix: 'bps',
      groupSeparator: '_',
      groupSize: 3,
    })
  }

  toFraction(maxDenominator?: number): [BigNumber, BigNumber] {
    return this.value.toFraction(maxDenominator)
  }

  toJSON(): string {
    return this.value.toJSON()
  }

  toNumber(): number {
    return this.value.toNumber()
  }

  toPrecision(significantDigits: number, roundingMode?: BigNumber.RoundingMode): string {
    return this.value.toPrecision(significantDigits, roundingMode)
  }

  toString(base?: number): string {
    return this.value.toString(base)
  }

  valueOf(): string {
    return this.value.valueOf()
  }
}

function getValue(value: Value): number | BigNumber {
  if (typeof value === 'number') {
    return value
  }
  return value.toBigNumber()
}

function numberLikeToBigNumber(value: NumberLike): BigNumber {
  if (NormalizedNumber.isInstance(value)) {
    return value.toBigNumber()
  }
  return bigNumberify(value)
}

function PercentageFunction(value: NumberLike, params?: PercentageOptions): PercentageClass {
  return new PercentageClass(value, params)
}

interface StaticPercentage {
  new (value: NumberLike, params?: PercentageOptions): Percentage
  (value: NumberLike, params?: PercentageOptions): Percentage
  readonly prototype: Percentage

  min(...values: Percentage[]): Percentage
  max(...values: Percentage[]): Percentage
  sum(...values: Percentage[]): Percentage
  sub(...values: Percentage[]): Percentage
  fromBips(value: NumberLike): Percentage
  fromPercentagePoints(value: number): Percentage
  isInstance(value: unknown): value is Percentage
}

Object.setPrototypeOf(PercentageFunction, PercentageClass)
PercentageFunction.prototype = PercentageClass.prototype

/**
 * Represents a percentage as a fraction number i.e. 0.5 (means 50%)
 * For percentages greater than 1, use the allowMoreThan1 flag.
 * For percentages less than 0, use the allowLessThan0 flag.
 * All percentage operations (plus, times etc.) return percentage that can be outside a 0 to 1 range.
 */
export const Percentage: StaticPercentage = Object.assign(PercentageFunction, PercentageClass)
